import { ColumnsType } from 'antd/es/table'
import { Dropdown } from 'components/Dropdown'
import { StatusWithDateCell } from '../../components/Table/Cells/StatusWithDateCell/StatusWithDateCell'
import { ROLES_NAMES } from 'constants/common'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import { fetchLedBy } from 'pages/CRUDUser/api'
import { LeadByIndicator } from '../../components/LeadByIndicator'
import { Sorter } from '../../components/Sorter/Sorter'
import { ColumnType, SortOrder } from 'antd/es/table/interface'
import {
  ContractCategoryStatus,
  TCCListColumnsFunc,
  TCCListFiltersFunc,
  TContractCategoriesList,
  TContractsCategoriesListFilters
} from './types'
import { optionFormatter } from '../Contracts/helpers'
import IconNameItem from '../../components/IconNameItem/IconNameItem'
import CategoriesPopover from './CategoriesPopover'
import { currencyFormatter } from '../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { ReactComponent as WarningIcon } from 'assets/svg/WarningRed16.svg'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { BTN_TXT, TOOLTIP } from '../../constants'
import { ReactComponent as More16 } from '../../assets/svg/More16.svg'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { Link } from '../../components/Link'
import { STATUS_TYPES_CLASSNAMES } from '../../components/Status'
import { getStatusFilterOptions } from './api'
import { GroupUsersView } from '../../components/GroupUsersView/GroupUsersView'

export const CATEGORY_STATUS_TO_CLASSNAME = {
  [ContractCategoryStatus.ACTIVE]: STATUS_TYPES_CLASSNAMES.GREEN,
  [ContractCategoryStatus.CONTRACTING]: STATUS_TYPES_CLASSNAMES.GRAY,
  [ContractCategoryStatus.EXPIRED]: STATUS_TYPES_CLASSNAMES.LIGHTRED,
  [ContractCategoryStatus.RFP]: STATUS_TYPES_CLASSNAMES.ORANGE,
  [ContractCategoryStatus.CREATED]: STATUS_TYPES_CLASSNAMES.LIGHTBLUE,
  [ContractCategoryStatus.LOADING]: STATUS_TYPES_CLASSNAMES.GRAY
} as const

export const CATEGORY_STATUS_NAME = {
  [ContractCategoryStatus.ACTIVE]: 'Active',
  [ContractCategoryStatus.CONTRACTING]: 'Contracting',
  [ContractCategoryStatus.EXPIRED]: 'Expired',
  [ContractCategoryStatus.RFP]: 'RFP',
  [ContractCategoryStatus.CREATED]: 'Created',
  [ContractCategoryStatus.LOADING]: 'Loading contracts'
} as const

export const CATEGORY_STATUS_TO_DATE_LABEL = {
  [ContractCategoryStatus.ACTIVE]: 'Ends on',
  [ContractCategoryStatus.CONTRACTING]: 'Starts on',
  [ContractCategoryStatus.EXPIRED]: 'Ended on',
  [ContractCategoryStatus.RFP]: 'Bidding started on',
  [ContractCategoryStatus.CREATED]: 'Set up on',
  [ContractCategoryStatus.LOADING]: 'Started on'
} as const

const _nameCol: ColumnType<TContractCategoriesList> = {
  dataIndex: 'name',
  key: 'name',
  width: 284,
  title: ({ sortOrder, sortColumn }) => (
    <Sorter<TContractCategoriesList>
      dataIndex={'name'}
      sortColumn={sortColumn}
      sortOrder={sortOrder as SortOrder}
    >
      Contract category
    </Sorter>
  ),
  sorter: true,
  showSorterTooltip: false
}

export const columns = ({
  applyFilters,
  filters,
  userUuid,
  showOrganization
}: TCCListColumnsFunc) => {
  const columns: ColumnsType<TContractCategoriesList> = [
    {
      ..._nameCol,
      render: (_, { name, uuid, led_by }) => (
        <CategoriesPopover
          name={name}
          uuid={uuid}
          led_by={led_by}
          categoriesList
        />
      )
    },
    {
      dataIndex: 'led_by',
      key: 'led_by',
      width: 160,
      title: 'Led by',
      className: 'vertical-align-middle',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(({ led_by, uuid }) => (
              <div key={uuid} className="separate-item row align-center">
                {led_by ? <LeadByIndicator value={led_by} /> : '-'}
              </div>
            ))
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      ),
      showSorterTooltip: false,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractsCategoriesListFilters,
          TCCListFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={fetchLedBy}
          field="led_by"
          asyncSearch
          optionFormatter={optionFormatter}
          {...props}
        />
      ),
      filterIcon: !!filters?.led_by?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true
    },
    {
      dataIndex: 'organization',
      key: 'organization',
      width: 235,
      title: 'Organization',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(({ organization }) => (
              <div
                key={organization?.uuid}
                className="separate-item row align-center"
              >
                {organization?.name ?? '-'}
              </div>
            ))
          ) : (
            <div className=" row align-center">-</div>
          )}
        </div>
      )
    },
    {
      dataIndex: 'responsible',
      key: 'responsible',
      width: 235,
      title: 'Sourcing owner',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(({ responsible }) =>
              responsible?.uuid ? (
                <div
                  key={responsible.uuid}
                  className="separate-item categories-responsible"
                >
                  <IconNameItem
                    firstName={responsible.first_name}
                    className="responsible"
                    lastName={responsible.last_name}
                    src={responsible.avatar?.file_preview || null}
                    roleName={ROLES_NAMES[responsible.role]}
                  />
                </div>
              ) : (
                <div className="separate-item row align-center">-</div>
              )
            )
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      )
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 200,
      title: 'Category status',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {led_by?.length ? (
            led_by?.map(
              ({
                status,
                created_at,
                earliest_contract_start_date,
                latest_contract_finish_date,
                status_change_date
              }) => {
                let date: string | null = null
                if (status === ContractCategoryStatus.CREATED) {
                  date = created_at
                } else if (
                  status === ContractCategoryStatus.CONTRACTING ||
                  status === ContractCategoryStatus.LOADING
                ) {
                  date = earliest_contract_start_date
                } else if (status === ContractCategoryStatus.RFP) {
                  date = status_change_date
                } else {
                  date = latest_contract_finish_date
                }
                return (
                  <div key={status} className="separate-item row align-center">
                    {status ? (
                      <StatusWithDateCell
                        statusName={CATEGORY_STATUS_NAME[status]}
                        statusClassName={CATEGORY_STATUS_TO_CLASSNAME[status]}
                        dateLabel={CATEGORY_STATUS_TO_DATE_LABEL[status]}
                        date={date}
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                )
              }
            )
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractsCategoriesListFilters,
          TCCListFiltersFunc,
          unknown
        >
          searchCallback={getStatusFilterOptions}
          applyFilters={applyFilters}
          appliedFilters={filters}
          asyncSearch
          optionFormatter={optionFormatter}
          field="status"
          {...props}
        />
      ),
      filterIcon: filters?.status?.length ? <FilterApplied16 /> : <Filter16 />,
      filterSearch: true
    },
    {
      dataIndex: 'compliance_requirement',
      key: 'compliance_requirement',
      width: 235,
      title: 'Compliance requirement, %',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(
              ({
                compliance_requirement,
                responsible,
                not_measuring_compliance
              }) => (
                <div className="separate-item row align-center">
                  {not_measuring_compliance ? (
                    <Tooltip title={TOOLTIP.NOT_MEASURING}>
                      Not measuring
                    </Tooltip>
                  ) : !!Number(compliance_requirement) ? (
                    compliance_requirement
                  ) : responsible?.uuid === userUuid ? (
                    <Tooltip title={TOOLTIP.COMPLIANCE_CC_EMPTY}>
                      <WarningIcon />
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </div>
              )
            )
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      )
    },
    {
      dataIndex: 'expected_spend',
      key: 'expected_spend',
      width: 235,
      title: 'Expected spend, $',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(({ expected_spend }) => (
              <div className="separate-item row align-center">
                {expected_spend ? currencyFormatter(expected_spend) : '-'}
              </div>
            ))
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      )
    },
    {
      dataIndex: 'expected_savings',
      key: 'expected_savings',
      width: 235,
      title: 'Expected savings, $',
      render: (_, { led_by }) => (
        <div className="flex column gap-16">
          {!!led_by?.length ? (
            led_by?.map(({ expected_savings, responsible }) => (
              <div className="separate-item row align-center">
                {!!expected_savings ? (
                  currencyFormatter(expected_savings)
                ) : responsible?.uuid === userUuid ? (
                  <Tooltip title={TOOLTIP.EXPECTED_SAVINGS_CC_EMPTY}>
                    <WarningIcon />
                  </Tooltip>
                ) : (
                  '-'
                )}
              </div>
            ))
          ) : (
            <div className="row align-center">-</div>
          )}
        </div>
      )
    }
  ]

  if (showOrganization) return columns

  return columns.filter((column) => column.key !== 'organization')
}

export const cpColumns = ({
  showResponsibleUser,
  openAssignPopup
}: {
  showResponsibleUser: boolean
  openAssignPopup: ({
    selectedCC
  }: {
    selectedCC: TContractCategoriesList
  }) => void
}): ColumnsType<TContractCategoriesList> => {
  if (showResponsibleUser)
    return [
      {
        ..._nameCol,
        width: '30%'
      },
      {
        title: 'Sourcing team',
        dataIndex: 'sourcing_team',
        key: 'sourcing_team',
        width: '30%',
        render: (_, { sourcing_team }) => {
          return (
            <>
              {sourcing_team?.length ? (
                <GroupUsersView
                  userList={sourcing_team}
                  popupTitle="Sourcing team"
                  showHiddenUserTooltip
                  // show one user full way if length === 1
                  // show 0 users full way if length is more than 1
                  fullInfoUsersCount={Number(sourcing_team.length === 1)}
                  hiddenUsersLeftCountMode="excludeVisibleGroupMembers"
                  showPopupUserCounter={false}
                />
              ) : (
                <div className="row align-center">-</div>
              )}
            </>
          )
        }
      },
      {
        title: 'Sourcing owner',
        dataIndex: 'sourcing_owner',
        key: 'sourcing_owner',
        width: '30%',
        render: (_, { sourcing_owner }) => {
          return (
            <>
              {sourcing_owner?.uuid ? (
                <Link to={`/my-community/profile/${sourcing_owner.uuid}`}>
                  <IconNameItem
                    firstName={sourcing_owner.first_name}
                    lastName={sourcing_owner.last_name}
                    src={sourcing_owner.avatar?.file_preview || null}
                    roleName={ROLES_NAMES[sourcing_owner.role]}
                  />
                </Link>
              ) : (
                <div className="row align-center">-</div>
              )}
            </>
          )
        }
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'action',
        width: '10%',
        fixed: 'right',
        render: (_, row) => (
          <div className="flex align-end justify-end full-width">
            <Button.Menu
              icon={<More16 />}
              type={BUTTON_TYPES.GHOST}
              options={[
                {
                  text: BTN_TXT.MANAGE_CATEGORY_ASSIGNMENT,
                  onClick: () => openAssignPopup({ selectedCC: row })
                }
              ]}
              iconOnly
              hideArrow
              medium
            />
          </div>
        )
      }
    ]
  return [_nameCol]
}
